import React, { useState } from 'react';
import { 
  Card, 
  Space, 
  Tag, 
  Image, 
  Typography, 
  Input, 
  Button,
  message,
  Steps,
  Tabs,
  List,
  Avatar,
  Modal,
  Checkbox
} from 'antd';
import { 
  ArrowLeftOutlined,
  UserOutlined,
  TeamOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { TextArea, Search } = Input;
const { TabPane } = Tabs;

interface PromotionItem {
  name: string;
  value: string;
}

interface PromotionPackage {
  id: string;
  name: string;
  image: string;
  items: PromotionItem[];
}

interface CustomerTag {
  id: string;
  name: string;
  count: number;
  customers: Customer[];
}

interface Customer {
  id: string;
  name: string;
  phone: string;
  tags: string[];
  avatar?: string;
}

// 模拟数据
const promotionPackages: PromotionPackage[] = [
  {
    id: '1',
    name: '尊享礼遇套餐',
    image: '/images/package1.jpg',
    items: [
      { name: '优惠券', value: '满1000减200' },
      { name: '专属服务', value: '24小时管家' },
      { name: '活动折扣', value: '85折' }
    ]
  },
  {
    id: '2',
    name: '豪华体验套餐',
    image: '/images/package2.jpg',
    items: [
      { name: '优惠券', value: '满2000减500' },
      { name: '专属服务', value: 'VIP专车接送' },
      { name: '活动折扣', value: '8折' }
    ]
  }
];

// 模拟客户数据
const customers: Customer[] = [
  { id: '1', name: '张先生', phone: '138****1234', tags: ['vip', 'frequent'] },
  { id: '2', name: '李女士', phone: '139****5678', tags: ['new'] },
  { id: '3', name: '王先生', phone: '137****9012', tags: ['vip', 'birthday'] },
  { id: '4', name: '赵女士', phone: '135****3456', tags: ['frequent'] },
  { id: '5', name: '刘先生', phone: '136****7890', tags: ['new', 'birthday'] }
];

const customerTags: CustomerTag[] = [
  { 
    id: 'vip', 
    name: 'VIP客户', 
    count: 128,
    customers: customers.filter(c => c.tags.includes('vip'))
  },
  { 
    id: 'frequent', 
    name: '常住客户', 
    count: 256,
    customers: customers.filter(c => c.tags.includes('frequent'))
  },
  { 
    id: 'new', 
    name: '新客户', 
    count: 512,
    customers: customers.filter(c => c.tags.includes('new'))
  },
  { 
    id: 'birthday', 
    name: '本月生日', 
    count: 64,
    customers: customers.filter(c => c.tags.includes('birthday'))
  }
];

const SendPromotionPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [greeting, setGreeting] = useState('');
  const [searchText, setSearchText] = useState('');
  const [customerPreviewVisible, setCustomerPreviewVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('tags');

  const steps = [
    { title: '选择套餐' },
    { title: '选择客户' },
    { title: '确认发送' }
  ];

  const getSelectedPackageInfo = () => {
    return promotionPackages.find(pkg => pkg.id === selectedPackage);
  };

  const getSelectedCustomersByTags = () => {
    const selectedTagCustomers = new Set<string>();
    selectedTags.forEach(tagId => {
      const tag = customerTags.find(t => t.id === tagId);
      tag?.customers.forEach(customer => {
        selectedTagCustomers.add(customer.id);
      });
    });
    return Array.from(selectedTagCustomers);
  };

  const getTotalSelectedCustomers = () => {
    if (activeTab === 'tags') {
      return getSelectedCustomersByTags().length;
    }
    return selectedCustomers.length;
  };

  const handleNext = () => {
    if (currentStep === 0 && !selectedPackage) {
      message.warning('请选择一个套餐');
      return;
    }
    if (currentStep === 1) {
      const total = getTotalSelectedCustomers();
      if (total === 0) {
        message.warning('请选择至少一个客户');
        return;
      }
    }
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate(-1);
    }
  };

  const handleSend = () => {
    message.success('发送成功！');
    navigate(-1);
  };

  // 渲染套餐选择步骤
  const renderPackageSelection = () => (
    <div className="package-selection">
      <div style={{ padding: '16px' }}>
        {promotionPackages.map(pkg => (
          <Card
            key={pkg.id}
            hoverable
            style={{ 
              marginBottom: '16px',
              borderColor: selectedPackage === pkg.id ? '#529C8A' : undefined
            }}
            onClick={() => setSelectedPackage(pkg.id)}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Image
                src={pkg.image}
                alt={pkg.name}
                style={{ width: '100%', height: 160, objectFit: 'cover' }}
              />
              <Title level={5}>{pkg.name}</Title>
              <div>
                {pkg.items.map((item, index) => (
                  <Tag key={index} color="blue">
                    {item.name}: {item.value}
                  </Tag>
                ))}
              </div>
            </Space>
          </Card>
        ))}
      </div>
    </div>
  );

  // 渲染客户选择步骤
  const renderCustomerSelection = () => (
    <div className="customer-selection" style={{ padding: '16px' }}>
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        style={{ marginBottom: 16 }}
      >
        <TabPane 
          tab={<span><TeamOutlined />按标签选择</span>} 
          key="tags"
        >
          <div className="tags-container">
            {customerTags.map(tag => (
              <Tag.CheckableTag
                key={tag.id}
                checked={selectedTags.includes(tag.id)}
                onChange={checked => {
                  if (checked) {
                    setSelectedTags([...selectedTags, tag.id]);
                  } else {
                    setSelectedTags(selectedTags.filter(id => id !== tag.id));
                  }
                }}
                style={{ marginBottom: '8px', fontSize: '14px' }}
              >
                {tag.name} ({tag.count}人)
              </Tag.CheckableTag>
            ))}
          </div>
          {selectedTags.length > 0 && (
            <Button 
              type="link" 
              onClick={() => setCustomerPreviewVisible(true)}
              style={{ padding: 0, marginTop: 8 }}
            >
              查看已选客户名单 ({getSelectedCustomersByTags().length}人)
            </Button>
          )}
        </TabPane>
        <TabPane 
          tab={<span><UserOutlined />选择具体客户</span>} 
          key="customers"
        >
          <Search
            placeholder="搜索客户姓名或手机号"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ marginBottom: 16 }}
          />
          <List
            dataSource={customers.filter(c => 
              c.name.includes(searchText) || 
              c.phone.includes(searchText)
            )}
            renderItem={customer => (
              <List.Item>
                <Checkbox
                  checked={selectedCustomers.includes(customer.id)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedCustomers([...selectedCustomers, customer.id]);
                    } else {
                      setSelectedCustomers(selectedCustomers.filter(id => id !== customer.id));
                    }
                  }}
                >
                  <Space>
                    <Avatar icon={<UserOutlined />} />
                    <div>
                      <div>{customer.name}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>{customer.phone}</div>
                    </div>
                    <div>
                      {customer.tags.map(tag => {
                        const tagInfo = customerTags.find(t => t.id === tag);
                        return tagInfo && (
                          <Tag key={tag} color="blue">{tagInfo.name}</Tag>
                        );
                      })}
                    </div>
                  </Space>
                </Checkbox>
              </List.Item>
            )}
          />
        </TabPane>
      </Tabs>
    </div>
  );

  // 渲染确认步骤
  const renderConfirmation = () => {
    const selectedPkg = getSelectedPackageInfo();
    const totalCustomers = getTotalSelectedCustomers();

    return (
      <div className="confirmation" style={{ padding: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Card title="套餐信息">
            <Space direction="vertical">
              <div>套餐名称：{selectedPkg?.name}</div>
              <div>包含权益：
                {selectedPkg?.items.map((item, index) => (
                  <Tag key={index} color="blue">
                    {item.name}: {item.value}
                  </Tag>
                ))}
              </div>
            </Space>
          </Card>

          <Card title="发送对象">
            <Space direction="vertical">
              {activeTab === 'tags' ? (
                <div>已选标签：
                  {selectedTags.map(tagId => {
                    const tag = customerTags.find(t => t.id === tagId);
                    return tag && (
                      <Tag key={tagId}>
                        {tag.name} ({tag.count}人)
                      </Tag>
                    );
                  })}
                </div>
              ) : (
                <div>已选客户：
                  {selectedCustomers.map(customerId => {
                    const customer = customers.find(c => c.id === customerId);
                    return customer && (
                      <Tag key={customerId}>
                        {customer.name}
                      </Tag>
                    );
                  })}
                </div>
              )}
              <div>总计发送人数：{totalCustomers}人</div>
            </Space>
          </Card>

          <Card title="个性化问候语">
            <TextArea
              value={greeting}
              onChange={e => setGreeting(e.target.value)}
              placeholder="请输入问候语（选填）"
              maxLength={200}
              showCount
              style={{ height: 100 }}
            />
          </Card>
        </Space>
      </div>
    );
  };

  // 客户预览弹窗
  const renderCustomerPreviewModal = () => {
    const selectedCustomerIds = getSelectedCustomersByTags();
    const selectedCustomerList = customers.filter(c => 
      selectedCustomerIds.includes(c.id)
    );

    return (
      <Modal
        title="已选客户名单"
        open={customerPreviewVisible}
        onCancel={() => setCustomerPreviewVisible(false)}
        footer={null}
        width={320}
      >
        <List
          dataSource={selectedCustomerList}
          renderItem={customer => (
            <List.Item>
              <Space>
                <Avatar icon={<UserOutlined />} />
                <div>
                  <div>{customer.name}</div>
                  <div style={{ fontSize: 12, color: '#999' }}>{customer.phone}</div>
                </div>
                <div>
                  {customer.tags.map(tag => {
                    const tagInfo = customerTags.find(t => t.id === tag);
                    return tagInfo && (
                      <Tag key={tag} color="blue">{tagInfo.name}</Tag>
                    );
                  })}
                </div>
              </Space>
            </List.Item>
          )}
        />
      </Modal>
    );
  };

  return (
    <div style={{ 
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#f5f5f5'
    }}>
      {/* 顶部标题栏 */}
      <div style={{ 
        padding: '12px 16px',
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Space>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handlePrev}
          />
          <Title level={4} style={{ margin: 0 }}>发送优惠</Title>
        </Space>
      </div>

      <Steps
        current={currentStep}
        items={steps}
        style={{ padding: '16px 0', background: '#fff' }}
      />

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {currentStep === 0 && renderPackageSelection()}
        {currentStep === 1 && renderCustomerSelection()}
        {currentStep === 2 && renderConfirmation()}
      </div>

      {/* 底部按钮 */}
      <div style={{
        padding: '12px 16px',
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Button onClick={handlePrev}>
          {currentStep === 0 ? '返回' : '上一步'}
        </Button>
        <Button 
          type="primary"
          onClick={currentStep === 2 ? handleSend : handleNext}
        >
          {currentStep === 2 ? '确认发送' : '下一步'}
        </Button>
      </div>

      {renderCustomerPreviewModal()}
    </div>
  );
};

export default SendPromotionPage;